import type { ReactNode } from "react";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import {
  formatAuditModule,
  formatRegistroAlteradoTipo,
  useAuditRecordDetail,
} from "@/modules/audit";
import type { AuditDetailStatus, AuditRecord } from "@/modules/audit";
import { formatAuditDateTime } from "../../audit-date-time";
import styles from "./audit-detail-dialog.module.scss";

export const AUDIT_DETAIL_TITLE = "Detalhes da Auditoria";
export const AUDIT_DETAIL_LOADING_MESSAGE = "Carregando os detalhes da auditoria...";
export const AUDIT_DETAIL_RETRY_LABEL = "Tentar novamente";
export const AUDIT_DETAIL_REAUTH_LABEL = "Entrar novamente";

/** Campo obrigatório que o serviço devolveu vazio, no padrão da tela de Usuários. */
const EMPTY_FIELD = "—";

interface AuditDetailDialogProps {
  /** Identificador da linha acionada em Detalhar (RN001). */
  readonly recordId: string;
  /** Única ação de fechamento do modal; devolve o foco à listagem (AC4). */
  readonly onClose: () => void;
  /** Fluxo de reautenticação acionado quando a sessão expira (401). */
  readonly onReauthenticate: () => void;
}

/** Iniciais do responsável no avatar, no mesmo formato do menu do usuário. */
function getInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";

  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts.at(-1)?.charAt(0) ?? "") : "";

  return `${first}${last}`.toUpperCase();
}

/** Props do Avatar do responsável: iniciais quando há nome, ícone genérico caso contrário. */
function getAvatarProps(record: AuditRecord): { label: string } | { icon: string } {
  if (record.responsavel) {
    return { label: getInitials(record.responsavel) };
  }

  return { icon: "pi pi-user" };
}

interface DetailFieldProps {
  readonly label: string;
  readonly testId: string;
  /**
   * Coluna do campo no card. O protótipo lê a alteração da esquerda para a
   * direita: Ação e Justificativa à esquerda, Valor Anterior e Novo Valor à
   * direita, cada par na sua linha.
   */
  readonly column?: "left" | "right";
  /** Linha explícita no grid: mantém Valor Anterior ao lado de Ação mesmo com o Registro Alterado entre eles no DOM. */
  readonly row?: 1 | 2 | 3;
  readonly children: ReactNode;
}

function DetailField({ label, testId, column = "left", row, children }: DetailFieldProps) {
  const rowClass = row ? styles[`row${row}`] : "";

  return (
    <div
      className={`${styles.field} ${column === "right" ? styles.right : styles.left} ${rowClass}`}
    >
      <dt className={styles.label}>{label}</dt>
      <dd className={styles.value} data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

/** Registro afetado pela alteração (usuário, perfil etc.), distinto do responsável pela ação. */
function renderRegistroAlterado(record: AuditRecord): ReactNode {
  if (!record.registroAlteradoNome) return null;

  const detalhe = record.registroAlteradoEmail || record.registroAlteradoCodigo;

  return (
    <div className={`${styles.field} ${styles.left} ${styles.row2}`}>
      <dt className={styles.label}>
        {record.registroAlteradoTipo
          ? formatRegistroAlteradoTipo(record.registroAlteradoTipo)
          : "Registro Alterado"}
      </dt>
      <dd className={styles.value} data-testid="audit-detail-registro-alterado">
        {record.registroAlteradoNome}
        {detalhe ? ` (${detalhe})` : ""}
      </dd>
    </div>
  );
}
interface UserLineProps {
  /** Ícone do dado, à esquerda do valor, como no protótipo. */
  readonly icon: string;
  readonly label: string;
  readonly testId: string;
  readonly children: ReactNode;
}

/** Linha de contato do responsável: ícone à esquerda e o valor ao lado. */
function UserLine({ icon, label, testId, children }: UserLineProps) {
  return (
    <div className={styles.userLine}>
      <dt className={styles.userTerm}>
        <i className={`${icon} ${styles.userIcon}`} aria-hidden />
        <span className={styles.srOnly}>{label}</span>
      </dt>
      <dd className={styles.userLineValue} data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

/** Data e hora do evento na mesma linha, como no protótipo. */
function renderDataHora(record: AuditRecord): string {
  if (!record.dataHora) return EMPTY_FIELD;

  const { date, time } = formatAuditDateTime(record.dataHora);

  return time ? `${date} ${time}` : date;
}
interface AuditDetailErrorStateProps {
  readonly status: Exclude<AuditDetailStatus, "loading" | "success">;
  readonly errorMessage: string | null;
  readonly onReauthenticate: () => void;
  readonly onRetry: () => void;
}

/** Estado de falha (sessão expirada, sem permissão, não encontrado ou erro genérico). */
function AuditDetailErrorState({
  status,
  errorMessage,
  onReauthenticate,
  onRetry,
}: AuditDetailErrorStateProps) {
  const icon =
    status === "forbidden" || status === "sessionExpired"
      ? "pi pi-lock"
      : "pi pi-exclamation-triangle";

  return (
    <div className={styles.state} role="alert" data-testid={`audit-detail-${status}`}>
      <i className={`${icon} ${styles.stateIcon}`} aria-hidden />
      <p className={styles.stateText}>{errorMessage}</p>

      {status === "sessionExpired" ? (
        <Button
          type="button"
          className={styles.stateAction}
          label={AUDIT_DETAIL_REAUTH_LABEL}
          icon="pi pi-sign-in"
          onClick={onReauthenticate}
          data-testid="audit-detail-reauth"
        />
      ) : null}

      {status === "error" ? (
        <Button
          type="button"
          className={styles.stateAction}
          label={AUDIT_DETAIL_RETRY_LABEL}
          icon="pi pi-refresh"
          onClick={onRetry}
          data-testid="audit-detail-retry"
        />
      ) : null}
    </div>
  );
}

/** Card com todas as informações do registro (protótipo): alteração, responsável e evento. */
function AuditDetailCard({ record }: { readonly record: AuditRecord }) {
  return (
    <div className={styles.card} data-testid="audit-detail-fields">
      {/*
       * A ordem no DOM intercala as colunas para que o grid monte as linhas
       * do protótipo — Ação/Valor Anterior e Justificativa/Novo Valor —
       * inclusive quando um dos campos condicionais não é exibido.
       */}
      <dl className={styles.grid}>
        <DetailField label="Ação" testId="audit-detail-acao" row={1}>
          <span className={styles.action}>{record.acao || EMPTY_FIELD}</span>
        </DetailField>

        {renderRegistroAlterado(record)}

        {/* Só quando o evento troca um valor; no primeiro preenchimento, apenas o novo. */}
        {record.valorAnterior ? (
          <DetailField
            label="Valor Anterior"
            testId="audit-detail-valor-anterior"
            column="right"
            row={1}
          >
            {record.valorAnterior}
          </DetailField>
        ) : null}

        {/* Só quando o evento tem justificativa registrada (AC2). */}
        {record.justificativa ? (
          <DetailField label="Justificativa" testId="audit-detail-justificativa" row={3}>
            {record.justificativa}
          </DetailField>
        ) : null}

        {record.valorNovo ? (
          <DetailField
            label="Novo Valor"
            testId="audit-detail-valor-novo"
            column="right"
            // Sem Valor Anterior (ex.: criação), Novo Valor sobe para a linha de Ação.
            row={record.valorAnterior ? 2 : 1}
          >
            {record.valorNovo}
          </DetailField>
        ) : null}
      </dl>

      <hr className={styles.divider} />

      {/* Abaixo da divisória: o responsável à esquerda, o evento à direita. */}
      <div className={styles.footer}>
        <div className={styles.user} data-testid="audit-detail-usuario">
          <p className={styles.label}>Responsável</p>

          <Avatar className={styles.avatar} shape="circle" {...getAvatarProps(record)} />

          <div className={styles.userInfo}>
            <p className={styles.userName}>{record.responsavel || EMPTY_FIELD}</p>

            <dl className={styles.userLines}>
              {record.responsavelEmail ? (
                <UserLine icon="pi pi-envelope" label="E-mail" testId="audit-detail-usuario-email">
                  {record.responsavelEmail}
                </UserLine>
              ) : null}

              {record.responsavelTelefone ? (
                <UserLine
                  icon="pi pi-phone"
                  label="Telefone"
                  testId="audit-detail-usuario-telefone"
                >
                  {record.responsavelTelefone}
                </UserLine>
              ) : null}

              {record.responsavelDetalhe ? (
                <UserLine icon="pi pi-users" label="Cargo" testId="audit-detail-usuario-cargo">
                  {record.responsavelDetalhe}
                </UserLine>
              ) : null}
            </dl>
          </div>
        </div>

        <dl className={styles.meta}>
          <DetailField label="Data / Hora" testId="audit-detail-data-hora">
            <span className={styles.dateTime}>{renderDataHora(record)}</span>
          </DetailField>

          <DetailField label="Módulo" testId="audit-detail-modulo">
            {record.modulo ? (
              <span className={styles.tag}>{formatAuditModule(record.modulo)}</span>
            ) : (
              EMPTY_FIELD
            )}
          </DetailField>
        </dl>
      </div>
    </div>
  );
}

/**
 * Modal somente leitura dos detalhes de um registro de auditoria.
 *
 * Sem ações de edição ou exclusão e sem nenhuma escrita na abertura ou no
 * fechamento (RN004/RN005): a consulta do registro é a única chamada feita.
 */
export function AuditDetailDialog({ recordId, onClose, onReauthenticate }: AuditDetailDialogProps) {
  const { status, record, errorMessage, reload } = useAuditRecordDetail(recordId);

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      contentClassName={styles.content}
      header={AUDIT_DETAIL_TITLE}
      visible
      modal
      draggable={false}
      resizable={false}
      dismissableMask
      ariaCloseIconLabel="Fechar"
      onHide={onClose}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="audit-detail-dialog"
    >
      {status === "loading" ? (
        <output className={styles.state} data-testid="audit-detail-loading">
          <i className={`pi pi-spinner pi-spin ${styles.stateIcon}`} aria-hidden />
          <p className={styles.stateText}>{AUDIT_DETAIL_LOADING_MESSAGE}</p>
        </output>
      ) : null}

      {/* Nenhuma falha exibe campo do registro: só a mensagem do cenário. */}
      {status !== "loading" && status !== "success" ? (
        <AuditDetailErrorState
          status={status}
          errorMessage={errorMessage}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
        />
      ) : null}

      {/* Todas as informações do registro reunidas em um card (protótipo). */}
      {record ? <AuditDetailCard record={record} /> : null}
    </Dialog>
  );
}
